import { useSuspenseQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ArrowLeft, ArrowRight, CalendarDays, ExternalLink } from "lucide-react";
import { Suspense, useEffect, useRef, useState } from "react";
import { NewsCommentSection } from "@/features/news/article/components/news-comment-section";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useWechatShare } from "@/hooks/use-wechat-share";
import { newsDetailQueryOptions } from "@/lib/client/apex-news";
import {
  getNewsDisplayLabel,
  parseNewsMarkdownForRender,
  toNewsAssetUrlForRender,
} from "@/lib/utils/apex-news";
import { formatNewsUtcDate } from "@/lib/utils/date";
import { COMMENT_SECTION_PLACEHOLDER_MIN_HEIGHT } from "@/features/news/article/constants";
import { NewsMarkdownArticle } from "@/features/news/article/markdown-article";
import { NewsReactionSection } from "@/features/news/article/reaction-section";
import { ThreadArticle } from "@/features/news/article/thread-article";
import type { CurrentUser } from "@/lib/client/user";
type NewsRouteUser = CurrentUser | null;

type NewsDetailPageProps = {
  articleSlug: string;
  currentUser: NewsRouteUser;
};

export function NewsDetailPage({ articleSlug, currentUser }: NewsDetailPageProps) {
  const { data } = useSuspenseQuery(newsDetailQueryOptions(articleSlug));
  const commentsAnchorRef = useRef<HTMLDivElement | null>(null);
  const [shouldLoadComments, setShouldLoadComments] = useState(false);
  const { detail, previous, next, recent } = data;
  const coverImage = toNewsAssetUrlForRender(detail.coverImage, {
    referer: detail.sourceUrl,
  });
  const parsedBody = parseNewsMarkdownForRender(detail.body);
  const normalizedBody = parsedBody.markdown;
  const headingAnchorIdByLine = parsedBody.headingAnchorIdByLine;
  const isTitleMissingDetail = !detail.title?.trim();
  const detailLabel = getNewsDisplayLabel({
    sourcePlatform: detail.sourcePlatform,
    sourceAuthorHandle: detail.sourceAuthorHandle,
    title: detail.title,
    fallback: "新闻详情",
  });
  const isThreadArticle =
    detail.contentFormat === "tweet-thread" &&
    Array.isArray(detail.threadPosts) &&
    detail.threadPosts.length > 0;
  const shareImage = coverImage || "/favicon.png";
  const shareTitle = detailLabel;
  const shareDescription = (detail.summary || detailLabel).replace(/\s+/g, " ").slice(0, 300);

  useWechatShare({
    enabled: true,
    title: `${shareTitle} - CN Apex Club`,
    desc: shareDescription,
    imgUrl: shareImage,
  });

  useEffect(() => {
    if (shouldLoadComments) {
      return;
    }

    const anchor = commentsAnchorRef.current;
    if (!anchor) {
      return;
    }

    if (typeof IntersectionObserver === "undefined") {
      setShouldLoadComments(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) {
            continue;
          }
          setShouldLoadComments(true);
          observer.disconnect();
          break;
        }
      },
      {
        root: null,
        rootMargin: "0px 0px 900px 0px",
        threshold: 0,
      },
    );

    observer.observe(anchor);

    return () => observer.disconnect();
  }, [shouldLoadComments]);

  return (
    <div className="space-y-8 pb-6">
      <Link
        to="/news"
        className="inline-flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="size-4" />
        返回新闻列表
      </Link>

      <Card className="relative overflow-hidden border-red-500/30 p-0">
        {coverImage ? (
          <img
            src={coverImage}
            alt={detailLabel}
            className="absolute inset-0 size-full object-cover"
            loading="lazy"
            decoding="async"
            referrerPolicy="no-referrer"
          />
        ) : (
          <div className="absolute inset-0 bg-linear-to-r from-red-700 via-red-600/70 to-zinc-900" />
        )}
        <div className="absolute inset-0 bg-linear-to-t from-black/90 via-black/75 to-black/20" />

        <CardContent className="relative z-10 space-y-3 p-6 md:p-8">
          <div className="flex flex-wrap items-center gap-2">
            <Badge className="bg-red-600 text-white hover:bg-red-600">{detail.sourceType}</Badge>
            <Badge variant="outline">{detail.sourcePlatform?.toUpperCase() || "EA"}</Badge>
            {detail.sourceAuthorHandle ? (
              <Badge variant="secondary">@{detail.sourceAuthorHandle}</Badge>
            ) : null}
            {detail.featured && (
              <Badge variant="secondary" className="bg-white/90 text-red-700">
                精选
              </Badge>
            )}
            <span className="inline-flex items-center gap-1 text-xs text-white/80">
              <CalendarDays className="size-3.5" />
              {formatNewsUtcDate(detail.publishingDate)}
            </span>
          </div>

          {isTitleMissingDetail ? null : (
            <>
              <h1 className="max-w-3xl text-2xl font-extrabold leading-tight text-white md:text-4xl text-shadow-md">
                {detailLabel}
              </h1>
              <p className="max-w-3xl whitespace-pre-line text-sm leading-relaxed text-white/90 md:text-base text-shadow-md">
                {detail.summary || "暂无摘要"}
              </p>
            </>
          )}

          <a
            href={detail.sourceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-sm font-semibold text-white/90 underline underline-offset-4 hover:text-white"
          >
            查看官方原文
            <ExternalLink className="size-4" />
          </a>
        </CardContent>
      </Card>

      {isThreadArticle ? (
        <ThreadArticle posts={detail.threadPosts} />
      ) : (
        <NewsMarkdownArticle
          normalizedBody={normalizedBody}
          headingAnchorIdByLine={headingAnchorIdByLine}
          sourceUrl={detail.sourceUrl}
          title={detailLabel}
        />
      )}

      <NewsReactionSection articleId={detail.id} currentUser={currentUser} />

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {previous ? (
          <Link to="/news/$slug" params={{ slug: previous.slug }} resetScroll>
            <Button
              variant="outline"
              className="h-auto w-full min-w-0 justify-start py-3 text-left"
            >
              <ArrowLeft className="size-4 shrink-0" />
              <span className="min-w-0 flex-1">
                <span className="block text-xs text-muted-foreground">上一篇</span>
                <span className="line-clamp-1">
                  {getNewsDisplayLabel({
                    sourcePlatform: previous.sourcePlatform,
                    sourceAuthorHandle: previous.sourceAuthorHandle,
                    title: previous.title,
                    fallback: "新闻",
                  })}
                </span>
              </span>
            </Button>
          </Link>
        ) : (
          <Button variant="outline" className="h-auto w-full justify-start py-3 text-left" disabled>
            <ArrowLeft className="size-4 shrink-0" />
            没有上一篇了
          </Button>
        )}

        {next ? (
          <Link to="/news/$slug" params={{ slug: next.slug }} resetScroll>
            <Button variant="outline" className="h-auto w-full min-w-0 justify-end py-3 text-right">
              <span className="min-w-0 flex-1">
                <span className="block text-xs text-muted-foreground">下一篇</span>
                <span className="line-clamp-1">
                  {getNewsDisplayLabel({
                    sourcePlatform: next.sourcePlatform,
                    sourceAuthorHandle: next.sourceAuthorHandle,
                    title: next.title,
                    fallback: "新闻",
                  })}
                </span>
              </span>
              <ArrowRight className="size-4 shrink-0" />
            </Button>
          </Link>
        ) : (
          <Button variant="outline" className="h-auto w-full justify-end py-3 text-right" disabled>
            没有下一篇了
            <ArrowRight className="size-4 shrink-0" />
          </Button>
        )}
      </div>

      <section className="space-y-2">
        <h2 className="text-base font-semibold">最近新闻</h2>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {recent.map((item) => {
            const isTitleMissing = !item.title?.trim();
            const itemLabel = getNewsDisplayLabel({
              sourcePlatform: item.sourcePlatform,
              sourceAuthorHandle: item.sourceAuthorHandle,
              title: item.title,
              fallback: "新闻",
            });

            return (
              <Link
                key={item.slug}
                to="/news/$slug"
                params={{ slug: item.slug }}
                className="block rounded-lg border p-3 transition-colors hover:bg-muted/50"
              >
                <p className="line-clamp-2 text-sm font-semibold leading-snug">{itemLabel}</p>
                {isTitleMissing && item.summary ? (
                  <p className="mt-1 line-clamp-2 whitespace-pre-line text-xs leading-relaxed text-muted-foreground">
                    {item.summary}
                  </p>
                ) : null}
                <p className="mt-1 text-xs text-muted-foreground">
                  {formatNewsUtcDate(item.publishingDate, { includeYear: false })}
                </p>
              </Link>
            );
          })}

          {recent.length === 0 && <p className="text-sm text-muted-foreground">暂无更多新闻</p>}
        </div>
      </section>

      <div ref={commentsAnchorRef}>
        {shouldLoadComments ? (
          <Suspense
            fallback={
              <section
                className="space-y-2 rounded-lg border border-dashed border-border/70 p-4"
                style={{ minHeight: COMMENT_SECTION_PLACEHOLDER_MIN_HEIGHT }}
              >
                <h2 className="text-sm font-semibold">评论区</h2>
                <p className="text-sm text-muted-foreground">评论加载中...</p>
              </section>
            }
          >
            <NewsCommentSection articleId={detail.id} currentUser={currentUser ?? null} />
          </Suspense>
        ) : (
          <section
            className="space-y-2 rounded-lg border border-dashed border-border/70 p-4"
            style={{ minHeight: COMMENT_SECTION_PLACEHOLDER_MIN_HEIGHT }}
          >
            <h2 className="text-sm font-semibold">评论区</h2>
            <p className="text-sm text-muted-foreground">滚动到这里后自动加载评论内容</p>
          </section>
        )}
      </div>
    </div>
  );
}
